import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { ContextMenu, type ContextMenuState } from "@/components/ui/ContextMenu";
import { Dialog } from "@/components/ui/Dialog";
import { NativeSelect } from "@/components/ui/NativeSelect";
import { SearchInput } from "@/pages/projekte/SearchInput";
import type { AiResolutionSuggestion, SupportTag, SupportTicket } from "@/lib/support";
import { markSupportTicketUnread, updateTicketStatus, updateTicketTodo } from "@/lib/support";
import { AdminDetailPanel } from "@/pages/support/AdminDetailPanel";
import { AdminKanbanBoard } from "@/pages/support/AdminKanbanBoard";
import { AdminTicketList } from "@/pages/support/AdminTicketList";
import {
  applyAdminFilters,
  buildOwnerOptions,
  preserveLocallyReadTickets,
  sortAdminTickets,
  ticketReadActivityKey,
  toastErr,
  toastOk,
  type AdminFilters,
} from "@/pages/support/utils";

const DEFAULT_FILTERS: AdminFilters = {
  urgency: "",
  status: "",
  program: "",
  reporter: "",
  todoState: "",
  todoOwner: "",
  tagId: "",
  quickView: "all",
  statFilter: "all",
  unreadOnly: false,
  search: "",
  sort: "smart",
};

type ViewMode = "board" | "list";

export function AdminView({
  tickets,
  tags,
  aiSuggestions,
  onlyOpen,
  onOnlyOpenChange,
  onRefresh,
  onNewTicket,
  onOpenHistory,
  currentLogin,
}: {
  tickets: SupportTicket[];
  tags: SupportTag[];
  aiSuggestions: AiResolutionSuggestion[];
  onlyOpen: boolean;
  onOnlyOpenChange: (v: boolean) => void;
  onRefresh: () => void;
  onNewTicket: () => void;
  onOpenHistory: (t: SupportTicket) => void;
  currentLogin: string;
}) {
  const [filters, setFilters] = useState<AdminFilters>(DEFAULT_FILTERS);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("board");
  const [localTickets, setLocalTickets] = useState(tickets);
  const [aiBannerHiddenAt, setAiBannerHiddenAt] = useState(0);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const readActivityRef = useRef<Map<number, string>>(new Map());

  useEffect(() => {
    const merged = preserveLocallyReadTickets(tickets, readActivityRef.current);
    readActivityRef.current = merged.readActivityById;
    setLocalTickets(merged.tickets);
  }, [tickets]);

  const patch = (p: Partial<AdminFilters>) => setFilters((prev) => ({ ...prev, ...p }));

  const filtered = useMemo(
    () =>
      sortAdminTickets(
        applyAdminFilters(localTickets, filters, currentLogin, aiSuggestions),
        filters.sort,
      ),
    [localTickets, filters, currentLogin, aiSuggestions],
  );

  const unreadCount = useMemo(
    () => localTickets.filter((t) => t.is_unread).length,
    [localTickets],
  );

  const selected =
    filtered.find((t) => t.id === selectedId) ??
    localTickets.find((t) => t.id === selectedId) ??
    null;

  useEffect(() => {
    if (selectedId != null && !localTickets.some((t) => t.id === selectedId)) {
      setSelectedId(null);
    }
  }, [localTickets, selectedId]);

  const bannerItems = useMemo(() => {
    return aiSuggestions.filter((s) => {
      const t = localTickets.find((row) => row.id === s.ticket_id);
      if (!t) return false;
      const st = String(t.status || "").toLowerCase();
      return st !== "closed" && st !== "resolved";
    });
  }, [aiSuggestions, localTickets]);

  const showBanner =
    bannerItems.length > 0 && Date.now() - aiBannerHiddenAt > 8 * 60 * 60 * 1000;

  const ownerOptions = useMemo(() => buildOwnerOptions(localTickets), [localTickets]);

  const patchTicket = useCallback((ticketId: number, patchFields: Partial<SupportTicket>) => {
    setLocalTickets((prev) =>
      prev.map((ticket) => {
        if (ticket.id !== ticketId) return ticket;
        if (patchFields.is_unread === false) {
          readActivityRef.current.set(ticketId, ticketReadActivityKey(ticket));
        } else if (patchFields.is_unread === true) {
          readActivityRef.current.delete(ticketId);
        }
        return { ...ticket, ...patchFields };
      }),
    );
  }, []);

  const markTicketUnread = useCallback(
    async (ticket: SupportTicket) => {
      if (ticket.is_unread) return;
      patchTicket(ticket.id, { is_unread: true });
      try {
        await markSupportTicketUnread(ticket.id);
        toastOk(`Ticket #${ticket.id} als ungelesen markiert`);
      } catch {
        patchTicket(ticket.id, { is_unread: false });
        toastErr("Konnte Ticket nicht als ungelesen markieren");
      }
    },
    [patchTicket],
  );

  const openTicketContextMenu = useCallback(
    (ticket: SupportTicket, event: MouseEvent) => {
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        items: [
          {
            id: "mark-unread",
            label: ticket.is_unread ? "Bereits ungelesen" : "Als ungelesen markieren",
            disabled: Boolean(ticket.is_unread),
            onClick: () => {
              void markTicketUnread(ticket);
            },
          },
        ],
      });
    },
    [markTicketUnread],
  );

  async function confirmAiResolution(ticketId: number, suggestion: AiResolutionSuggestion) {
    const note = String(suggestion.confirm_message || "").trim();
    const target = suggestion.suggested_status === "closed" ? "closed" : "resolved";
    const ok = window.confirm(
      `Ticket #${ticketId} als „${target === "closed" ? "geschlossen" : "gelöst"}“ markieren?\n\n` +
        `KI-Begründung: ${suggestion.reason || ""}`,
    );
    if (!ok) return;
    try {
      await updateTicketStatus(ticketId, target, note);
      await updateTicketTodo(ticketId, {
        todo_state: suggestion.suggested_todo_state || "erledigt",
        todo_owner_login: "",
      });
      toastOk("Ticket abgeschlossen");
      onRefresh();
    } catch {
      toastErr("Konnte Ticket nicht abschließen");
    }
  }

  return (
    <div className="support-admin space-y-4">
      {showBanner ? (
        <div className="rounded-ios-lg border border-status-green/40 bg-status-green/10 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-label">
              KI-Vorschlag · {bannerItems.length} Ticket{bannerItems.length === 1 ? "" : "s"}
            </span>
            <Button type="button" variant="secondary" className="text-xs" onClick={() => setAiBannerHiddenAt(Date.now())}>
              Ausblenden
            </Button>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {bannerItems.slice(0, 6).map((s) => (
              <div key={s.ticket_id} className="rounded-ios border border-separator/60 bg-card p-2 text-sm">
                <div className="font-medium">
                  #{s.ticket_id} {s.title}
                </div>
                <p className="text-xs text-label-secondary line-clamp-2">{s.reason}</p>
                <div className="mt-2 flex gap-2">
                  <Button type="button" className="text-xs" onClick={() => void confirmAiResolution(s.ticket_id, s)}>
                    Bestätigen
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="text-xs"
                    onClick={() => setSelectedId(s.ticket_id)}
                  >
                    Öffnen
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <SearchInput
            value={filters.search}
            onChange={(search) => patch({ search })}
            placeholder="Ticket suchen (Titel, Melder, Tag, Notiz…)"
            className="flex-1"
          />
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-ios border border-separator bg-card p-0.5">
              <button
                type="button"
                className={clsx(
                  "rounded-ios px-3 py-1.5 text-xs font-semibold transition",
                  viewMode === "board" ? "bg-accent text-label-on-accent" : "text-label-secondary hover:bg-fill-tertiary",
                )}
                onClick={() => setViewMode("board")}
              >
                Board
              </button>
              <button
                type="button"
                className={clsx(
                  "rounded-ios px-3 py-1.5 text-xs font-semibold transition",
                  viewMode === "list" ? "bg-accent text-label-on-accent" : "text-label-secondary hover:bg-fill-tertiary",
                )}
                onClick={() => setViewMode("list")}
              >
                Liste
              </button>
            </div>
            <Button type="button" variant="secondary" onClick={onRefresh} title="Neu laden">
              ↻
            </Button>
            <Button type="button" onClick={onNewTicket}>
              Neues Ticket
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <FilterChip
            active={filters.unreadOnly}
            onClick={() => patch({ unreadOnly: !filters.unreadOnly })}
            label={unreadCount > 0 ? `Ungelesen (${unreadCount})` : "Ungelesen"}
            emphasize={unreadCount > 0}
          />
          <FilterChip
            active={filters.todoOwner === "me"}
            onClick={() =>
              patch({ todoOwner: filters.todoOwner === "me" ? "" : "me", statFilter: "all" })
            }
            label="Meine"
          />
          <FilterChip
            active={filters.todoOwner === "unassigned"}
            onClick={() =>
              patch({
                todoOwner: filters.todoOwner === "unassigned" ? "" : "unassigned",
                statFilter: "all",
              })
            }
            label="Nicht zugewiesen"
          />
          <FilterChip
            active={filters.urgency === "critical"}
            onClick={() =>
              patch({ urgency: filters.urgency === "critical" ? "" : "critical", statFilter: "all" })
            }
            label="Super dringend"
          />
          <label className="ml-1 flex items-center gap-2 text-sm text-label-secondary">
            <input
              type="checkbox"
              checked={onlyOpen}
              onChange={(e) => onOnlyOpenChange(e.target.checked)}
              className="rounded border-separator"
            />
            nur offene
          </label>
          <span className="text-xs text-label-tertiary tabular-nums">
            {filtered.length} angezeigt
          </span>
        </div>

        {(tags || []).length ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs font-semibold uppercase text-label-tertiary">Themen</span>
            <FilterChip
              active={!filters.tagId}
              onClick={() => patch({ tagId: "" })}
              label="Alle"
            />
            {(tags || []).map((tag) => (
              <FilterChip
                key={tag.id}
                active={filters.tagId === String(tag.id)}
                onClick={() =>
                  patch({ tagId: filters.tagId === String(tag.id) ? "" : String(tag.id) })
                }
                label={tag.name}
              />
            ))}
          </div>
        ) : null}
      </div>

      {viewMode === "board" ? (
        <AdminKanbanBoard
          tickets={filtered}
          selectedId={selectedId}
          onSelect={(t) => setSelectedId(t.id)}
          onTicketPatched={patchTicket}
          onTicketContextMenu={openTicketContextMenu}
        />
      ) : (
        <div className="support-inbox rounded-ios-lg border border-separator bg-card">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-separator px-3 py-2">
            <span className="text-xs text-label-secondary tabular-nums">
              {filtered.length} Ticket{filtered.length === 1 ? "" : "s"}
            </span>
            <NativeSelect
              selectSize="sm"
              className="text-xs py-1"
              value={filters.sort}
              onChange={(e) => patch({ sort: e.target.value as AdminFilters["sort"] })}
              aria-label="Sortierung"
            >
              <option value="smart">Smart</option>
              <option value="newest">Neueste</option>
              <option value="oldest">Älteste</option>
              <option value="urgency">Dringlichkeit</option>
            </NativeSelect>
          </div>
          <div className="p-2">
            <AdminTicketList
              items={filtered}
              selectedId={selectedId}
              aiSuggestions={aiSuggestions}
              onSelect={(t) => setSelectedId(t.id)}
              onTicketContextMenu={openTicketContextMenu}
            />
          </div>
        </div>
      )}

      <Dialog
        open={selected != null}
        onClose={() => setSelectedId(null)}
        size="3xl"
        fullScreenMobile
        title={selected ? `Ticket #${selected.id}` : undefined}
        description={selected?.title || undefined}
        panelClassName="h-[min(92dvh,64rem)]"
        bodyClassName="!p-0 min-h-0 flex-1 overflow-y-auto"
      >
        {selected ? (
          <AdminDetailPanel
            ticket={selected}
            currentLogin={currentLogin}
            ownerOptions={ownerOptions}
            onRefresh={onRefresh}
            onOpenHistory={onOpenHistory}
            onConfirmAiResolution={confirmAiResolution}
            onTicketPatched={patchTicket}
            variant="modal"
          />
        ) : null}
      </Dialog>

      <ContextMenu state={contextMenu} onClose={() => setContextMenu(null)} />
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  emphasize,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  emphasize?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "rounded-full px-3 py-1 text-xs font-semibold transition",
        active
          ? "bg-accent text-label-on-accent"
          : emphasize
            ? "bg-accent-soft/40 text-accent ring-1 ring-inset ring-accent/35 hover:bg-accent-soft/60"
            : "bg-card text-label-secondary ring-1 ring-inset ring-separator hover:bg-fill-tertiary",
      )}
    >
      {label}
    </button>
  );
}
